import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
    GraduationCap,
    Search,
    RefreshCcw,
    Users,
    ArrowUpRight,
    Layers,
    Mail,
    Phone,
} from "lucide-react";
import { getTeachersOverview } from "../../utils/admin";

const SectionHeader = ({ children, accent = "from-[#2148C0] to-blue-400" }) => (
    <h3 className="text-xl md:text-2xl font-bold text-gray-800 flex items-center">
        <div className={`w-2 h-7 md:h-8 bg-gradient-to-b ${accent} rounded-full mr-3`} />
        {children}
    </h3>
);

const TeachersPage = () => {
    const [teachers, setTeachers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [query, setQuery] = useState("");
    const [sortBy, setSortBy] = useState("students");

    const load = async () => {
        setLoading(true);
        setError(null);
        try {
            const token = localStorage.getItem("token");
            const res = await getTeachersOverview(token);
            setTeachers(res?.data || []);
        } catch (err) {
            console.error(err);
            setError("Failed to load teachers.");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
    }, []);

    const totals = useMemo(() => {
        const totalStudents = teachers.reduce(
            (acc, t) => acc + (t.studentCount || 0),
            0
        );
        // classCount = unique year-groups + teacher-owned groups. yearGroup IS
        // a class in the school sense, so when the backend doesn't yet send
        // classCount we count "at least 1" per teacher who has students.
        const totalClasses = teachers.reduce((acc, t) => {
            const v = deriveClassCount(t);
            return acc + (v === null ? 1 : v);
        }, 0);
        return { totalStudents, totalClasses, teachers: teachers.length };
    }, [teachers]);

    const filtered = useMemo(() => {
        const q = query.trim().toLowerCase();
        let list = teachers;
        if (q) {
            list = list.filter((t) => {
                const name = `${t.firstName || ""} ${t.lastName || ""}`.toLowerCase();
                return (
                    name.includes(q) ||
                    (t.email || "").toLowerCase().includes(q) ||
                    (t.school || "").toLowerCase().includes(q)
                );
            });
        }
        list = [...list].sort((a, b) => {
            if (sortBy === "students") return (b.studentCount || 0) - (a.studentCount || 0);
            if (sortBy === "classes") {
                const va = deriveClassCount(a);
                const vb = deriveClassCount(b);
                return (vb === null ? 1 : vb) - (va === null ? 1 : va);
            }
            if (sortBy === "name") {
                const an = `${a.firstName || ""} ${a.lastName || ""}`.toLowerCase();
                const bn = `${b.firstName || ""} ${b.lastName || ""}`.toLowerCase();
                return an.localeCompare(bn);
            }
            return 0;
        });
        return list;
    }, [teachers, query, sortBy]);

    return (
        <div>
            {/* Hero */}
            <div className="mb-8 bg-gradient-to-r from-[#2148C0] to-blue-600 rounded-2xl p-6 md:p-8 text-white relative overflow-hidden shadow-lg shadow-[#2148C0]/20">
                <div className="absolute -top-20 -right-20 w-48 h-48 bg-white/10 rounded-full" />
                <div className="absolute -bottom-16 -left-16 w-32 h-32 bg-white/5 rounded-full" />
                <div className="relative z-10 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                    <div>
                        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/15 border border-white/20 text-white text-xs font-medium mb-3">
                            <GraduationCap size={13} /> Teachers Directory
                        </div>
                        <h1 className="text-3xl md:text-4xl font-bold tracking-tight">
                            Teachers &amp; their students
                        </h1>
                        <p className="text-blue-100 mt-1">
                            Every teacher, how many students they have, and the classes they run.
                        </p>
                    </div>
                    <button
                        onClick={load}
                        className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-white text-[#2148C0] hover:bg-blue-50 text-sm font-semibold shadow"
                    >
                        <RefreshCcw size={15} /> Refresh
                    </button>
                </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
                <Summary
                    icon={GraduationCap}
                    label="Teachers"
                    value={totals.teachers}
                    color="bg-purple-50 text-purple-600"
                />
                <Summary
                    icon={Users}
                    label="Students linked"
                    value={totals.totalStudents}
                    color="bg-blue-50 text-[#2148C0]"
                />
                <Summary
                    icon={Layers}
                    label="Active classes"
                    value={totals.totalClasses}
                    color="bg-green-50 text-green-600"
                />
            </div>

            {/* Controls */}
            <div className="flex flex-col sm:flex-row gap-3 mb-6">
                <div className="relative flex-1">
                    <Search
                        size={16}
                        className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
                    />
                    <input
                        type="text"
                        placeholder="Search teachers by name, school or email..."
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        className="w-full pl-9 pr-4 py-2.5 rounded-xl bg-white border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#2148C0]/30 focus:border-[#2148C0]"
                    />
                </div>
                <select
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value)}
                    className="px-3 py-2.5 rounded-xl bg-white border border-gray-200 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-[#2148C0]/30 focus:border-[#2148C0]"
                >
                    <option value="students">Most students</option>
                    <option value="classes">Most classes</option>
                    <option value="name">Name (A–Z)</option>
                </select>
            </div>

            {error && (
                <div className="mb-4 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-red-700 text-sm">
                    {error}
                </div>
            )}

            <SectionHeader accent="from-blue-500 to-indigo-600">
                Your teachers
            </SectionHeader>

            <div className="mt-5">
                {loading ? (
                    <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-6">
                        {Array.from({ length: 6 }).map((_, i) => (
                            <div
                                key={i}
                                className="h-44 rounded-2xl bg-white border border-gray-100 animate-pulse"
                            />
                        ))}
                    </div>
                ) : filtered.length === 0 ? (
                    <div className="rounded-2xl border border-dashed border-gray-300 bg-white py-16 text-center">
                        <GraduationCap className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                        <p className="text-gray-500 font-medium">No teachers found.</p>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-6">
                        {filtered.map((t) => (
                            <TeacherCard key={t.user_id} t={t} />
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

const Summary = ({ icon: Icon, label, value, color }) => (
    <div className="flex items-center gap-4 rounded-2xl bg-white border border-gray-100 p-5 shadow-sm">
        <span className={`flex h-12 w-12 items-center justify-center rounded-xl ${color}`}>
            <Icon size={20} />
        </span>
        <div>
            <p className="text-xs uppercase tracking-[0.16em] text-gray-500 font-semibold">
                {label}
            </p>
            <p className="text-3xl font-bold text-gray-900 mt-0.5">
                {Number(value || 0).toLocaleString()}
            </p>
        </div>
    </div>
);

const Avatar = ({ t }) => {
    const initials = `${(t.firstName || "?").charAt(0)}${(t.lastName || "").charAt(0)}`.toUpperCase();
    if (t.profile_image) {
        return (
            <img
                src={t.profile_image}
                alt={initials}
                className="h-12 w-12 rounded-xl object-cover border border-gray-200"
                onError={(e) => {
                    e.target.style.display = "none";
                }}
            />
        );
    }
    return (
        <div className="h-12 w-12 rounded-xl bg-gradient-to-br from-[#2148C0] to-blue-500 text-white flex items-center justify-center font-bold text-lg">
            {initials}
        </div>
    );
};

// Pick the best class-count value we can from whatever shape the list
// endpoint sends. Older deploys only return `groupCount`, which leads to
// "0 classes" for teachers who have students but no in-app groups —
// misleading because each yearGroup is itself a class.
const deriveClassCount = (t) => {
    if (typeof t.classCount === "number") return t.classCount;
    if (typeof t.yearGroupCount === "number")
        return t.yearGroupCount + (t.groupCount || 0);
    // Backend hasn't been redeployed with the new fields. If the teacher
    // has any students, they have at least one class (their yearGroup);
    // signal "click through for the real number" with an em-dash.
    if ((t.studentCount || 0) > 0) return null;
    return t.groupCount || 0;
};

const TeacherCard = ({ t }) => {
    const classCount = deriveClassCount(t);
    return (
        <Link
            to={`/teachers/${t.user_id}/classes`}
            className="group block bg-white border border-gray-100 rounded-2xl shadow-sm hover:shadow-lg hover:-translate-y-0.5 hover:border-blue-200 transition-all duration-300 p-6"
        >
            <div className="flex items-start justify-between mb-4">
                <Avatar t={t} />
                <ArrowUpRight
                    size={18}
                    className="text-gray-300 group-hover:text-[#2148C0] transition-colors"
                />
            </div>

            <h3 className="text-lg font-bold text-gray-900 group-hover:text-[#2148C0] transition-colors truncate">
                {t.firstName} {t.lastName}
            </h3>
            <p className="text-sm text-gray-600 truncate">{t.school || "—"}</p>

            <div className="mt-4 grid grid-cols-2 gap-2">
                <div className="rounded-xl bg-blue-50 px-3 py-2.5">
                    <p className="text-[10px] uppercase tracking-[0.16em] text-[#2148C0]/80 font-semibold">
                        Students
                    </p>
                    <p className="text-xl font-bold text-[#2148C0]">
                        {(t.studentCount || 0).toLocaleString()}
                    </p>
                </div>
                <div className="rounded-xl bg-green-50 px-3 py-2.5">
                    <p className="text-[10px] uppercase tracking-[0.16em] text-green-700/80 font-semibold">
                        Classes
                    </p>
                    <p
                        className="text-xl font-bold text-green-700"
                        title={
                            classCount === null
                                ? "Open the teacher to see their classes."
                                : undefined
                        }
                    >
                        {classCount === null
                            ? "—"
                            : classCount.toLocaleString()}
                    </p>
                </div>
            </div>

            <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-500">
                {t.email && (
                    <span className="inline-flex items-center gap-1.5">
                        <Mail size={12} /> {t.email}
                    </span>
                )}
                {t.phoneNumber && (
                    <span className="inline-flex items-center gap-1.5">
                        <Phone size={12} /> {t.phoneNumber}
                    </span>
                )}
            </div>
        </Link>
    );
};

export default TeachersPage;
